import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { GameAudioProvider } from '@/shared/hooks/useGameAudioSettings';
import { useBackgroundMusic } from '../hooks/useBackgroundMusic';
import { useGameSession } from '../hooks/useGameSession';
import { MeasurementGameScreen } from '../types';

import GameStyleBoundary from '@/shared/components/gameStyleBoundary/GameStyleBoundary';
import Spinner from '@/shared/components/spinner/Spinner';

import '../styles/measurement-game.scss';

import type { CSSProperties } from 'react';
import type { GameAudioProps } from '@/features/gameSession';
import type { NpcFactUpdateHandler } from '../types';
import type { SessionOptions } from '../hooks/useGameSession';

export type MeasurementGameProps = GameAudioProps &
	SessionOptions & {
		className?: string;
		style?: CSSProperties;
		totalStars?: number;
		onUpdateUserData?: NpcFactUpdateHandler;
	};

const StartScreen = lazy(() => import('./screens/StartScreen'));
const GameScreen = lazy(() => import('./screens/GameScreen'));
const EndScreen = lazy(() => import('./screens/EndScreen'));
const WorldBackground = lazy(() => import('./screens/WorldBackground'));

const MeasurementGameContent = (props: MeasurementGameProps) => {
	const { t } = useTranslation();
	useBackgroundMusic();

	const {
		activeScreen,
		gameLoading,
		totalAmount,
		outcome,
		npcSessionRef,
		level,
		world,
		introMessages,
		onStart,
		onReplay,
		onBack,
		onAnswer,
		onFinish,
		onCoinCollect,
		onLoadingChange,
		onGameplayEvent,
	} = useGameSession(props);

	const { className, style, totalStars, onUpdateUserData, levelProgress } = props;

	let screen = <StartScreen onStart={onStart} onBack={onBack} totalAmount={totalAmount} />;

	if (activeScreen === MeasurementGameScreen.Game) {
		screen = (
			<GameScreen
				onGameplayEvent={onGameplayEvent}
				npcSessionRef={npcSessionRef}
				totalStars={totalStars ?? totalAmount}
				onUpdateUserData={onUpdateUserData}
				onLoadingChange={onLoadingChange}
				introMessages={introMessages}
				level={level}
				totalAmount={totalAmount}
				world={world}
				onAnswer={onAnswer}
				onFinish={onFinish}
				onBack={onBack}
				onCoinCollect={onCoinCollect}
			/>
		);
	} else if (activeScreen === MeasurementGameScreen.End) {
		screen = (
			<EndScreen
				outcome={outcome}
				totalAmount={totalAmount}
				levelProgress={levelProgress}
				onReplay={onReplay}
				onClose={onBack}
			/>
		);
	}

	const isGameplay = activeScreen === MeasurementGameScreen.Game;
	const loadingScreen = (
		<div className={classNames('measurement-game__loading', { 'measurement-game__loading--world': isGameplay })}>
			{isGameplay && (
				<Suspense fallback={null}>
					<WorldBackground world={world} className="measurement-game__loading-background" />
				</Suspense>
			)}
			<div
				className="measurement-game__loading-indicator"
				role="status"
				aria-label={t('measurement_game.game.loading')}
			>
				<Spinner classname="measurement-game__loading-spinner" />
			</div>
		</div>
	);

	return (
		<GameStyleBoundary>
			<div className={classNames('measurement-game', className)} style={style} data-screen={activeScreen}>
				{isGameplay && gameLoading && loadingScreen}
				<Suspense fallback={isGameplay ? null : loadingScreen}>{screen}</Suspense>
			</div>
		</GameStyleBoundary>
	);
};

const MeasurementGame = ({ audioOn, onAudioChange, ...props }: MeasurementGameProps) => (
	<GameAudioProvider audioOn={audioOn} onAudioChange={onAudioChange}>
		<MeasurementGameContent {...props} />
	</GameAudioProvider>
);

export default MeasurementGame;
